/**
 * Tiền tố đường dẫn của hai cổng.
 *
 * Mỗi cổng có một nhánh URL riêng (ADR-UI-0012, sửa lần 1):
 *
 * - `/admin/...`  — cổng nền tảng, các màn sinh từ `app/modules.ts`
 * - `/client/...` — cổng tenant, các nhóm nghiệp vụ ở `nav-config.ts`
 *
 * Hai registry vẫn khai **đường dẫn gốc** không mang tiền tố (`/tenant`,
 * `/tri-thuc`). Tiền tố được ghép đúng ở tệp này, nên đổi tên nhánh là sửa hai
 * hằng dưới đây chứ không phải sửa ~130 chuỗi rải khắp kho.
 */
import type { PhamViCong } from './pham-vi-cong';

/** Tiền tố của từng cổng. Đổi tên nhánh URL: sửa đúng hai dòng này. */
export const TIEN_TO: Readonly<Record<PhamViCong, string>> = {
  'nen-tang': '/admin',
  tenant: '/client',
};

/**
 * Đường dẫn dùng chung, **không** mang tiền tố cổng nào.
 *
 * Màn đăng nhập và các màn cài đặt tài khoản phục vụ cả hai cổng; nhét chúng
 * vào một nhánh sẽ buộc người ở cổng kia nhảy sang nhánh không phải của mình
 * chỉ để đổi mật khẩu.
 *
 * ⚠️ `/` **không** nằm trong danh sách này. Nó là cửa vào, không phải một màn
 * dùng chung: `ghep('/', 'tenant')` phải ra `/client` — gốc nhánh tenant, nơi
 * có dashboard — chứ không giữ nguyên `/`. Để `/` ở đây thì mục `Dashboard`
 * (khai `href: '/'`) không được ghép tiền tố, bấm vào là rơi về `TrangGoc` và
 * bị đẩy theo *cổng của tài khoản*, nên nhân sự nền tảng đang xem `/client`
 * lại bị văng sang `/admin`.
 */
export const DUNG_CHUNG: readonly string[] = [
  '/cai-dat',
  '/cai-dat/api-keys',
  '/dang-nhap',
  '/quen-mat-khau',
  '/dat-lai-mat-khau',
];

/** Đường dẫn này nằm ngoài hệ tiền tố. */
export function laDungChung(duongDanGoc: string): boolean {
  return DUNG_CHUNG.includes(duongDanGoc);
}

/**
 * Ghép tiền tố cổng vào một đường dẫn gốc.
 *
 * `ghep('/tenant', 'nen-tang')` → `/admin/tenant`.
 * Đường dẫn dùng chung và đường dẫn **đã mang tiền tố** trả về nguyên vẹn —
 * nhờ vậy gọi nhầm hai lần không sinh ra `/client/client/tri-thuc`.
 */
export function ghep(duongDanGoc: string, phamVi: PhamViCong): string {
  if (laDungChung(duongDanGoc)) return duongDanGoc;
  if (daCoTienTo(duongDanGoc)) return duongDanGoc;
  // `/` là gốc nhánh: nối thẳng sẽ ra `/client/`, một đường dẫn khác với
  // `/client` trong mắt `NavLink` nên mục `Dashboard` không bao giờ sáng.
  if (duongDanGoc === '/') return TIEN_TO[phamVi];
  return `${TIEN_TO[phamVi]}${duongDanGoc}`;
}

/** Đường dẫn đã mang tiền tố của một cổng nào đó. */
export function daCoTienTo(duongDan: string): boolean {
  return Object.values(TIEN_TO).some(
    (t) => duongDan === t || duongDan.startsWith(`${t}/`),
  );
}

/**
 * Tách một đường dẫn đầy đủ thành cổng và đường dẫn gốc.
 *
 * `/admin/tenant` → `{ phamVi: 'nen-tang', goc: '/tenant' }`.
 * Không mang tiền tố thì `phamVi` là `undefined` và `goc` là chính nó.
 */
export function tach(duongDan: string): { phamVi?: PhamViCong; goc: string } {
  for (const [phamVi, tienTo] of Object.entries(TIEN_TO) as [PhamViCong, string][]) {
    if (duongDan === tienTo) return { phamVi, goc: '/' };
    if (duongDan.startsWith(`${tienTo}/`)) {
      return { phamVi, goc: duongDan.slice(tienTo.length) };
    }
  }
  return { goc: duongDan };
}
